<script lang="ts">
	import AuthenticationMenu from "@components/AuthenticationMenu.svelte";
	import HeaderCartLink from "@components/HeaderCartLink.svelte";
	import Logo from "@components/Logo.svelte";
	import Toast from "@components/Toast.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import { flip } from "svelte/animate";
	import { cubicIn, cubicOut } from "svelte/easing";
	import { fly } from "svelte/transition";
	import hamburgerIcon from "@core/assets/icons/hamburger.svg?raw";
	import logoutIcon from "@core/assets/icons/logout.svg?raw";
	import plusIcon from "@core/assets/icons/plus.svg?raw";
	import userIcon from "@core/assets/icons/user.svg?raw";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { getAccountDetailsPath } from "@core/schema/paths/getAccountDetailsPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { toasts } from "../../state/toasts.svelte.js";
	import translations from "./Header.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const { transparent: initialTransparent }: { transparent: boolean } = $props();
	const { menu, tenant } = getGlobalTemplateProps();

	let scrollY = $state(0);
	const signedIn = $derived(Auth.signedIn);

	let mobileMenuOpen = $state(false);
	let searchbarOpen = $state(false);
	let authMenuOpen = $state(false);

	let header = $state<HTMLElement>();

	async function signOut(): Promise<void> {
		await Auth.signOut();
		authMenuOpen = false;
	}

	const transparent = $derived(
		initialTransparent && scrollY < 16 && !mobileMenuOpen && !searchbarOpen && !authMenuOpen,
	);

	let mounted = $state(false);

	function openModalFromQuery(): void {
		const url = new URL(location.href);
		const modal = url.searchParams.get("modal");

		if (isEnum(AuthenticationModalKind)(modal)) {
			Auth.openAuthenticationModal(modal, {
				searchParams: url.searchParams,
			});
		} else if (modal === "cart") {
			Cart.showModal = true;
		} else {
			url.searchParams.delete("modal");
			history.replaceState(null, "", url.toString());
		}
	}

	$effect(() => {
		if (!mounted) {
			return;
		}

		const url = new URL(location.href);

		if (Cart.showModal) {
			url.searchParams.set("modal", "cart");
			Auth.closeAuthenticationModal();
		} else if (Auth.authenticationModalKind !== undefined) {
			url.searchParams.set("modal", Auth.authenticationModalKind);
		} else {
			url.searchParams.delete("modal");
		}

		history.replaceState(null, "", url.toString());
	});

	function handleWindowMouseup(event: MouseEvent): void {
		if (!(event.target instanceof Node) || !header?.contains(event.target)) {
			authMenuOpen = false;
		}
	}

	onMount(() => {
		openModalFromQuery();
		mounted = true;
	});
</script>

<svelte:window bind:scrollY onpopstate={openModalFromQuery} onmouseup={handleWindowMouseup} />

<header
	class={["flex flex-col items-stretch top-0 z-40 w-full", initialTransparent ? "fixed" : "sticky"]}
	bind:this={header}
>
	<div
		class={[
			"h-14 lg:h-20 flex items-center justify-center z-40 transition-colors duration-75 border-b",
			transparent ? "text-white border-transparent" : "bg-white border-b-fcrlightgray-300 text-fcrdarkbrown",
		]}
	>
		<div class="flex items-center w-full max-w-[105rem] justify-between lg:grid grid-cols-[1fr_auto_1fr] px-4 lg:px-6">
			<div class="flex items-center min-w-fit">
				<a href={getHomepagePath(tenant)} aria-label={translate("logoAria")}>
					<Logo
						class={[
							"w-30 h-7",
							searchbarOpen && "hidden min-[500px]:flex",
							transparent ? "text-white" : "text-fcrdarkgray",
						]}
					/>
				</a>
				<!-- Non-MVP feature: web search -->
				<!-- <AlgoliaSearchbar {transparent} bind:open={searchbarOpen} /> -->
			</div>
			<nav
				class={[
					"hidden lg:flex gap-10 font-antipol justify-center items-center justify-self-center px-6",
					searchbarOpen && "invisible xl:visible",
				]}
			>
				{#each menu as item (item)}
					<a href={item.href} class="hover:text-fcrbrown transition-colors font-medium text-base/tighter">
						<Formatted text={item.name} />
					</a>
				{/each}
			</nav>
			<div class="flex gap-6 lg:gap-2 lg:justify-self-end relative">
				<!-- Non-MVP feature: web search -->
				<!--
			import zoomIcon from "@core/assets/icons/zoom.svg?raw";
			...
			<button
				type="button"
				class={["size-6", searchbarOpen ? "hidden" : "lg:hidden"]}
				onclick={(): void => {
					searchbarOpen = !searchbarOpen;
				}}
			>
				<Icon icon={zoomIcon} class="size-6" />
			</button>
			-->
				<HeaderCartLink transparentHeader={transparent} class={[searchbarOpen && "hidden xl:flex"]} />
				<button
					type="button"
					class="size-6 lg:hidden"
					onclick={(): void => {
						if (searchbarOpen) {
							searchbarOpen = false;
						} else {
							mobileMenuOpen = !mobileMenuOpen;
						}
					}}
				>
					{#if mobileMenuOpen || searchbarOpen}
						<Icon icon={plusIcon} class="size-6 rotate-45" aria-label={translate("closeMenu")} />
					{:else}
						<Icon icon={hamburgerIcon} class="size-6" aria-label={translate("openMenu")} />
					{/if}
				</button>
				{#if signedIn}
					<button
						type="button"
						class="bg-fcrbeige hover:border-fcrgray hover:border hidden rounded-full lg:flex items-center justify-center size-14"
						onclick={(): void => {
							authMenuOpen = !authMenuOpen;
						}}
					>
						<Icon icon={userIcon} class="size-6 text-fcrbrown" />
					</button>
				{:else}
					<Button
						class="lg:flex hidden transition-colors lg:h-12 xl:h-auto"
						variant={transparent ? ButtonVariant.White : ButtonVariant.Dark}
						size={ButtonSize.Medium}
						onclick={(): void => {
							Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, {});
						}}
					>
						<Formatted text={translate("login")} />
					</Button>
				{/if}
			</div>
		</div>
	</div>
	<div
		class={[
			"flex flex-col bg-white px-4 shadow-xl z-40 fixed top-14 xl:top-20 w-full",
			mobileMenuOpen ? "lg:hidden" : "hidden",
		]}
	>
		{#each menu as item (item)}
			<a href={item.href} class="text-1.5xl/tighter text-fcrdarkgray font-antipol py-6">
				<Formatted text={item.name} />
			</a>
			<hr class="border-t-fcrlightgray" />
		{/each}
		<div class="flex justify-between items-center">
			{#if signedIn}
				<a
					href={getAccountDetailsPath(tenant)}
					class="w-full grow shrink text-1.5xl/tighter text-fcrdarkgray font-antipol py-6"
				>
					<Formatted text={translate("customerAccount")} />
				</a>
				<button
					type="button"
					class="border-l border-fcrlightgray flex items-center justify-center h-14 w-16 grow-0 shrink-0"
					onclick={signOut}
				>
					<Icon icon={logoutIcon} class="size-6" />
				</button>
			{:else}
				<button
					class="w-full grow shrink text-1.5xl/tighter text-fcrdarkgray font-antipol py-6 text-left"
					onclick={(): void => {
						Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, {});
					}}
				>
					<Formatted text={translate("login")} />
				</button>
			{/if}
		</div>
	</div>
	{#if authMenuOpen}
		<AuthenticationMenu
			class="w-75 sm:ml-auto mr-2 mt-2 transition-[opacity] duration-300 fixed top-20 right-0 z-[121] hidden lg:flex"
			onsignout={signOut}
		/>
		<button
			type="button"
			onclick={(): void => {
				authMenuOpen = false;
			}}
			aria-label={translate("closeMenu")}
			class="fixed top-20 z-[39] lg:z-[120] inset-0 bg-fcrdarkgray/80 appearance-none cursor-auto"
		></button>
	{/if}
</header>
<section class="fixed top-14 lg:top-20 z-[100] grid w-full pt-2 pointer-events-none px-6">
	{#each toasts.items as toast (toast.id)}
		<div
			class="w-full *:pointer-events-auto flex justify-end col-start-1 row-start-1"
			animate:flip={{ duration: 300 }}
			in:fly={{ y: 40, duration: 300, easing: cubicOut }}
			out:fly={{ y: -40, duration: 200, easing: cubicIn }}
		>
			<Toast {toast} />
		</div>
	{/each}
</section>
